/**
 * A comprehensive pluralisation utility for React applications
 * Handles various English pluralisation rules and custom formatting
 */

/**
 * Basic pluralisation rules for common English words
 */
const IRREGULAR_PLURALS: Record<string, string> = {
    child: 'children',
    person: 'people',
    man: 'men',
    woman: 'women',
    tooth: 'teeth',
    foot: 'feet',
    mouse: 'mice',
    goose: 'geese',
    ox: 'oxen',
    datum: 'data',
    medium: 'media',
    curriculum: 'curricula',
    criterion: 'criteria',
    phenomenon: 'phenomena',
    analysis: 'analyses',
    basis: 'bases',
    crisis: 'crises',
    thesis: 'theses',
    // Add more irregular plurals as needed
};

/**
 * Words that don't change between singular and plural
 */
const UNCOUNTABLE_WORDS: readonly string[] = [
    'sheep',
    'deer',
    'fish',
    'species',
    'series',
    'information',
    'equipment',
    'furniture',
    'advice',
    'news',
    'research',
    'progress',
    'homework',
    'software',
    'hardware',
    'data',
    'water',
    'rice',
    'money',
    'music',
    'air',
    'love',
];

/**
 * Apply standard English pluralisation rules
 */
function applyStandardRules(word: string): string {
    const lowerWord = word.toLowerCase();

    // Handle irregular plurals
    if (IRREGULAR_PLURALS[lowerWord]) {
        return IRREGULAR_PLURALS[lowerWord];
    }

    // Handle uncountable words
    if (UNCOUNTABLE_WORDS.includes(lowerWord)) {
        return word;
    }

    // Standard pluralisation rules
    if (
        lowerWord.endsWith('s') ||
        lowerWord.endsWith('ss') ||
        lowerWord.endsWith('sh') ||
        lowerWord.endsWith('ch') ||
        lowerWord.endsWith('x') ||
        lowerWord.endsWith('z')
    ) {
        return word + 'es';
    }

    if (lowerWord.endsWith('y') && !'aeiou'.includes(lowerWord[lowerWord.length - 2])) {
        return word.slice(0, -1) + 'ies';
    }

    if (lowerWord.endsWith('f')) {
        return word.slice(0, -1) + 'ves';
    }

    if (lowerWord.endsWith('fe')) {
        return word.slice(0, -2) + 'ves';
    }

    if (lowerWord.endsWith('o') && !'aeiou'.includes(lowerWord[lowerWord.length - 2])) {
        return word + 'es';
    }

    // Default: just add 's'
    return word + 's';
}

/**
 * Main pluralise function
 * @param {string} word - The word to pluralise
 * @param {number} count - The count to determine if pluralisation is needed
 * @param {boolean} includeCount - Whether to include the count in the result
 * @returns {string} The pluralised word with optional count
 */
export function pluralise(word: string, count: number = 2, includeCount: boolean = false): string {
    if (typeof word !== 'string') {
        throw new Error('Word must be a string');
    }

    if (typeof count !== 'number' || count < 0) {
        throw new Error('Count must be a non-negative number');
    }

    const result = Math.abs(count) === 1 ? word : applyStandardRules(word);

    return includeCount ? `${count} ${result}` : result;
}

/**
 * Format function that creates a complete sentence with count and pluralised word
 * @param {number} count - The count
 * @param {string} word - The word to pluralise
 * @param {string} verb - Optional verb (defaults to "is/are")
 * @returns {string} Formatted sentence
 */
export function formatCount(count: number, word: string, verb: string | null = null): string {
    const pluralisedWord = pluralise(word, count, true);

    if (verb) {
        const pluralVerb = count === 1 ? verb : verb === 'is' ? 'are' : verb + 's';
        return `${pluralisedWord} ${pluralVerb}`;
    }

    return pluralisedWord;
}

/**
 * React hook for pluralisation
 * @param {string} word - The word to pluralise
 * @param {number} count - The count
 * @param {boolean} includeCount - Whether to include count
 * @returns {string} Pluralised result
 */
export function usePluralise(word: string, count: number, includeCount: boolean = false): string {
    return pluralise(word, count, includeCount);
}

/**
 * Utility for handling zero, one, or many scenarios
 * @param {number} count - The count
 * @param {string} zeroText - Text for zero count
 * @param {string} oneText - Text for count of one
 * @param {string} manyText - Text for count greater than one (use {count} placeholder)
 * @returns {string} Formatted text
 */
export function conditionalFormat(
    count: number,
    zeroText: string,
    oneText: string,
    manyText: string
): string {
    if (count === 0) return zeroText;
    if (count === 1) return oneText;
    return manyText.replace('{count}', count.toString());
}

/**
 * Configuration options for formatPlural function
 */
export interface FormatPluralOptions {
    word: string;
    zero?: string;
    one?: string;
    many?: string;
}

/**
 * Advanced formatter with custom templates
 * @param {number} count - The count
 * @param {FormatPluralOptions} options - Configuration options
 * @returns {string} Formatted result
 */
export function formatPlural(count: number, options: FormatPluralOptions): string {
    const {
        word,
        zero = `No ${pluralise(word, 2)}`,
        one = `1 ${word}`,
        many = `{count} ${pluralise(word, 2)}`,
    } = options;

    if (!word) {
        throw new Error('Word is required in options');
    }

    if (count === 0) return zero;
    if (count === 1) return one;
    return many.replace('{count}', count.toString());
}

// Default export for convenience
export default pluralise;
